import { Badge, Button, Col, Input, InputNumber, Popconfirm, Row, Space, Table, Tabs, message } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marketplaces, unitTestType } from 'helpers';
import { addSizeTiersShippingWeightAsin, addUnitTestNote, calculateSizeTiersShippingWeight, deleteSizeTiersShippingWeightAsin, getSizeTiersShippingWeightAsins, getUniteTestNote, updateSizeTiersShippingWeightAsin, updateUnitTestNote } from 'services/unit-tests';
import { getIdFromObject } from 'utils';
import { Helmet } from 'react-helmet';

const { TabPane } = Tabs;

interface DataType {
  _id: string;
  asin: string | null;
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  expectedSizeTier: string | null;
  sizeTierLibrary: string | null;
  expectedShipWgt: number | null;
  shipWgtLibrary: number | null;
  match: boolean;
  marketplace: string;
}

const initNewAsin = {
  asin: null,
  weight: null,
  length: null,
  width: null,
  height: null,
  expectedSizeTier: null,
  sizeTierLibrary: null,
  expectedShipWgt: null,
  shipWgtLibrary: null,
  match: false,
  marketplace: "US"
}

const SizeTiersShippingWeight = () => {
  const [activedTab, setActivedTab] = useState<string>("US");
  const [dataTable, setDataTable] = useState<any>([]);
  const [noteObj, setNoteObj] = useState<any>();
  const [note, setNote] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [newAsin, setNewAsin] = useState<Omit<DataType, "_id">>(initNewAsin)

  let { marketplace = "US" } = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    getUniteTestNote(unitTestType.SIZETIERSANDSHIPWGT, marketplace)
      .then(({ data }: any) => setNoteObj((prev: any) => {
        if (data.data && Array.isArray(data.data) && data.data.length > 0) {
          setNote(data.data[0].note)
          return data.data[0]
        }
        setNote(null)
        return null
      }))
      .catch(() =>
        setNote("Here is a note")
      )

    getSizeTiersShippingWeightAsins(marketplace)
      .then(({ data }) => {
        // API now returns _id as string, no need to parse
        const asins = (data.data && Array.isArray(data.data)) ? data.data : []
        setDataTable(asins)
      })
      .catch(() =>
        message.error("Cannot get asins, please refresh page again")
      )
  }, [activedTab])

  useEffect(() => {
    setActivedTab(marketplace)
  }, [marketplace]);

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../${tabKey}`, { replace: true });
  };

  const handleOnSaveNote = () => {
    const query = {
      name: "Fulfillment Fee",
      note: note,
      numOfASINs: 0,
      passed: false
    }
    if (!noteObj) {
      addUnitTestNote(unitTestType.SIZETIERSANDSHIPWGT, marketplace, query)
        .then(({ data }) => {
          message.success("Added note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot add note"))
    } else {
      const id = noteObj._id // API now returns _id as string
      updateUnitTestNote(id, query)
        .then(({ data }) => {
          message.success("Updated note succesfully")
          if (data.data && Array.isArray(data.data) && data.data.length > 0) {
            setNoteObj(data.data[0])
          }
        })
        .catch(() => message.error("Cannot update note"))
    }
  }

  const handleOnDelete = (id: string) => {
    return deleteSizeTiersShippingWeightAsin(id)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted ASIN successfully")
      })
      .catch(() => message.error("Cannot delete ASIN"))
  }

  const handleOnCaculateAsin = (arrAsins: any, isCreate: boolean = true) => {
    setLoading(true)
    const query = arrAsins.reduce((arr: any[], current: any) => {
      let params: any = {
        weight: current.weight,
        length: current.length,
        width: current.width,
        height: current.height,
        marketplace,
        expectedSizeTier: current.expectedSizeTier,
        expectedShipWgt: current.expectedShipWgt,
        _id: current._id // API now returns _id as string
      }

      arr.push(params)
      return arr
    }, [])

    calculateSizeTiersShippingWeight(query)
      .then(({ data }: any) => {
        const result = data.reduce((arr: [any], current: any, currentIndex: number) => {
          let newAsinData = {
            ...query[currentIndex],
            asin: arrAsins[currentIndex].asin,
            sizeTierLibrary: current.sizeTier,
            shipWgtLibrary: current.shippingWeight,
            match:
              current.sizeTier === query[currentIndex].expectedSizeTier &&
              current.shippingWeight === query[currentIndex].expectedShipWgt
          }
          arr.push(newAsinData)
          return arr
        }, [])

        if (isCreate) {
              return addSizeTiersShippingWeightAsin(marketplace, result[0])
            .then(({ data }) => {
              // API now returns _id as string
              const newAsin = data.data
              setDataTable((prev: any) => {
                setNewAsin(initNewAsin)
                return [newAsin, ...prev]
              });
            })
            .catch(() => message.error('Cannot add new ASIN'))
        }

        return updateSizeTiersShippingWeightAsin(result)
          .then(({ data }) => {
            message.success(data.message)

            setDataTable(
              (prev: any) => {
                if (arrAsins.length === 1) {
                  return prev.map((d: any) => {
                    const id = arrAsins[0]._id // API now returns _id as string
                    const idData = d._id
                    return idData === id ? { ...d, ...result[0] } : d
                  })
                }
                return prev.map((d: any, index: number) => {
                  const id = arrAsins[index]._id // API now returns _id as string
                  const idData = d._id
                  return idData === id ? { ...d, ...result[index] } : d
                })
              }
            );
          })
          .catch(() => message.error('Cannot update ASIN'))
      })
      .catch(error => console.log(error))
      .finally(() => setLoading(false))
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      render: text => <a>{text}</a>,
    },
    {
      title: 'Unit Wgt',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: 'Length',
      dataIndex: 'length',
      key: 'length',
    },
    {
      title: 'Width',
      dataIndex: 'width',
      key: 'width',
    },
    {
      title: 'Height',
      dataIndex: 'height',
      key: 'height',
    },
    {
      title: 'Expected Size Tier',
      dataIndex: 'expectedSizeTier',
      key: 'expectedSizeTier',
    },
    {
      title: 'Size Tier - Lib',
      dataIndex: 'sizeTierLibrary',
      key: 'sizeTierLibrary',
    },
    {
      title: 'Expected Ship Wgt',
      dataIndex: 'expectedShipWgt',
      key: 'expectedShipWgt',
    },
    {
      title: 'Ship Wgt - Lib',
      dataIndex: 'shipWgtLibrary',
      key: 'shipWgtLibrary',
    },
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      render: value => (
        <Badge
          text={value ? "Yes" : "No"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => {
        return (
          <Space size="middle">
            <Button type="primary" onClick={() => handleOnCaculateAsin([record], false)}>Library</Button>
            <Popconfirm
              placement="topRight"
              title="Are you sure to delete this ASIN?"
              onConfirm={() => handleOnDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger />
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Size Tiers & Shipping Weight | Unit Test</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Unit Test for Size Tiers & Shipping Weight"
        footer={
          <Tabs defaultActiveKey={activedTab} type="card" activeKey={activedTab} onChange={handleOnChangeTab}>
            {marketplaces.map(m => (
              <TabPane tab={m.label} key={m.key}>
                <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
                  <Input.Group>
                    <Row gutter={8}>
                      <Col span={18}>
                        <label><strong>Note:</strong></label>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} />
                      </Col>
                      <Col span={5} offset={1} style={{ display: 'flex' }}>
                        <Space align="end">
                          <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => handleOnSaveNote()}
                          >
                            Save
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Space>
                    <label><strong>Test ASINs:</strong></label>
                    <Button
                      type="primary"
                      htmlType="submit"
                      onClick={() => handleOnCaculateAsin(dataTable, false)}
                    >
                      Calculate all ASINs using OUR library
                    </Button>
                  </Space>
                  <Input.Group>
                    <Row gutter={2}>
                      <Col span={2}>
                        <Input
                          placeholder="Enter ASIN*"
                          value={newAsin.asin || undefined}
                          onChange={(e: any) => setNewAsin((prev: any) => ({ ...prev, asin: e.target.value }))}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Unit Wgt"
                          value={newAsin.weight || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, weight: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Length"
                          value={newAsin.length || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, length: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Width"
                          value={newAsin.width || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, width: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2}>
                        <InputNumber
                          controls={false}
                          placeholder="Height"
                          value={newAsin.height || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, height: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={3}>
                        <Input
                          placeholder="Expected size tier"
                          value={newAsin.expectedSizeTier || undefined}
                          onChange={(e: any) => setNewAsin((prev: any) => ({ ...prev, expectedSizeTier: e.target.value }))}
                        />
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          controls={false}
                          placeholder="Expected Shipping Wgt"
                          value={newAsin.expectedShipWgt || undefined}
                          onChange={(value: any) => setNewAsin((prev: any) => ({ ...prev, expectedShipWgt: value }))}
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col span={2} offset={1}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          onClick={() => handleOnCaculateAsin([newAsin])}
                        >
                          Calculate Ours
                        </Button>
                      </Col>
                    </Row>
                  </Input.Group>
                  <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
                </Space>
              </TabPane>
            ))}
          </Tabs>
        }
      />
    </>
  )
};

export default SizeTiersShippingWeight;